import { useRoute, Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { MapPin, Users, Clock, Briefcase, Send, Bookmark, ShieldCheck, CheckCircle2, Loader2, X } from "lucide-react";
import { Badge, Feature } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useListing } from "../lib/useListing";
import { setMeta, resetMeta } from "../lib/seo";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { SaveButton } from "../components/SaveButton";
import { MobileActionBar } from "../components/MobileActionBar";
import { jobs as mockJobs } from "../data/mock";

export default function JobDetail() {
  const [, params] = useRoute("/j/:id");
  const id = params?.id || "";
  const { item: j, state } = useListing("jobs", id, mockJobs);
  const { user } = useAuth();
  const [, navigate] = useLocation();
  useEffect(() => {
    if (j) setMeta({ title: `${j.title} — KontrataPH`, description: [j.company, j.city, j.salary].filter(Boolean).join(" · ") || undefined });
    return () => resetMeta();
  }, [j]);
  const [myApp, setMyApp] = useState<any | null>(null);
  const [showApply, setShowApply] = useState(false);
  const [coverNote, setCoverNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isWorker = !!user && (user.capabilities || []).includes("find_work");
  const isOwner = !!user && !!j?.ownerUserId && j.ownerUserId === user.id;
  const jobOpen = (j?.status || "open") === "open";

  useEffect(() => {
    if (user && j?.id && isWorker && !isOwner) api.myJobApplication(j.id).then(setMyApp).catch(() => setMyApp(null));
  }, [user, j?.id, isWorker, isOwner]);

  if (state === "loading") return <DetailLoading label="Loading job…" />;
  if (state === "notfound" || !j) return <DetailNotFound title="Job not found" backHref="/marketplace?tab=Jobs" backLabel="Browse Jobs" />;

  const tags: string[] = Array.isArray(j.tags) ? j.tags : [];
  const activeStatuses = ["applied", "shortlisted", "accepted"];
  const hasActiveApp = myApp && activeStatuses.includes(myApp.status);

  async function apply() {
    setBusy(true); setErr(null);
    try { const r = await api.applyToJob(j.id, coverNote.trim() || undefined); setMyApp(r?.application); setShowApply(false); setCoverNote(""); }
    catch (e: any) { setErr(e?.message || "Could not apply."); }
    finally { setBusy(false); }
  }
  async function withdraw() {
    setBusy(true); setErr(null);
    try { const r = await api.withdrawApplication(j.id); setMyApp(r?.application); }
    catch (e: any) { setErr(e?.message || "Could not withdraw."); }
    finally { setBusy(false); }
  }

  function ApplyBox() {
    if (!user) return <button onClick={() => navigate("/login")} className="btn-red w-full"><Send className="w-4 h-4" />Sign in to apply</button>;
    if (isOwner) return <div className="text-sm text-mut text-center">This is your job posting. See applicants below.</div>;
    if (!isWorker) return <div className="text-sm text-mut">Enable <b>Find Work</b> on your account to apply for jobs.</div>;
    if (!user.workerVerified) return (
      <div className="text-center">
        <p className="text-sm text-mut mb-2">Only verified workers can apply.</p>
        <Link href="/get-verified" className="btn-red w-full"><ShieldCheck className="w-4 h-4" />Get verified</Link>
      </div>
    );
    if (hasActiveApp) {
      const label: Record<string, string> = { applied: "Applied", shortlisted: "Shortlisted", accepted: "Hired 🎉" };
      return (
        // QA Tab3 #17: chip + a compact "Withdraw" sit in a row so the buttons never
        // overflow the mobile action-bar card.
        <div className="flex items-center gap-2 w-full min-w-0">
          <div className={`chip flex-1 min-w-0 justify-center py-2 ${myApp.status === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-navy-50 text-navy"}`}>{label[myApp.status]}</div>
          {myApp.status !== "accepted" && <button onClick={withdraw} disabled={busy} className="btn-outline h-9 px-3 text-xs text-brand-red shrink-0 whitespace-nowrap disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-3.5 h-3.5" />}Withdraw</button>}
        </div>
      );
    }
    if (!jobOpen) return <div className="chip w-full justify-center py-2 bg-navy-50 text-mut">Applications closed</div>;
    return <button onClick={() => setShowApply(true)} className="btn-red w-full"><Send className="w-4 h-4" />{myApp ? "Apply again" : "Apply Now"}</button>;
  }

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <div className="text-sm text-mut mb-4"><Link href="/marketplace?tab=Jobs" className="hover:text-navy">Jobs</Link> / {j.title}</div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <div className="space-y-4">
          <div className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><Briefcase className="w-6 h-6" /></div>
              <div>
                <h1 className="text-2xl font-extrabold text-navy">{j.title}</h1>
                <p className="text-mut">{j.company}</p>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-mut">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{j.city}</span>
                  <span>{j.type}</span>
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" />{j.applicants} applicants</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{j.posted}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">{tags.map((t) => <Badge key={t}>{t}</Badge>)}</div>
          </div>

          <div className="card p-5"><h3 className="font-bold text-navy mb-2">Job Description</h3>
            <p className="text-sm text-mut">{j.company} is hiring a {j.title} in {j.city}. This is a {String(j.type).toLowerCase()} role. Apply now to be considered.</p>
          </div>
          <div className="card p-5"><h3 className="font-bold text-navy mb-3">Responsibilities</h3>
            <ul className="grid sm:grid-cols-2 gap-2">
              {(tags.length ? tags : ["On-site work", "Team collaboration"]).map((t) => <Feature key={t}>{t}</Feature>)}
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card p-5">
            <div className="text-xs text-mut">Offered salary</div>
            <div className="text-xl font-extrabold text-navy">{j.salary || "Negotiable"}</div>
            <div className="flex flex-col gap-2 mt-4">
              <ApplyBox />
              <SaveButton kind="job" refId={j.id} className="w-full h-10 border border-line" />
            </div>
            {err && <p className="text-xs text-brand-red text-center mt-2">{err}</p>}
            <p className="text-[11px] text-mut flex items-center gap-1 justify-center mt-3"><ShieldCheck className="w-3 h-3" />Verified employer</p>
          </div>
          {isOwner && <OwnerApplicants jobId={j.id} />}
          <div className="card p-5"><h3 className="font-bold text-navy mb-3">At a glance</h3>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-mut">Employment</dt><dd className="font-semibold text-navy">{j.type}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Location</dt><dd className="font-semibold text-navy">{j.city}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Applicants</dt><dd className="font-semibold text-navy">{j.applicants}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Posted</dt><dd className="font-semibold text-navy">{j.posted}</dd></div>
            </dl>
            <div className="mt-3 pt-3 border-t border-line">
              <div className="text-sm text-mut flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green" />Company profile available</div>
            </div>
          </div>
        </div>
      </div>

      {!isOwner && (
        <MobileActionBar>
          <div className="shrink min-w-0 max-w-[38%]">
            <div className="text-[11px] text-mut leading-none">Salary</div>
            <div className="text-sm font-bold text-navy leading-tight truncate">{j.salary || "Negotiable"}</div>
          </div>
          <div className="flex-1 min-w-0"><ApplyBox /></div>
        </MobileActionBar>
      )}

      {showApply && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setShowApply(false)}>
          <div className="card p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-navy">Apply — {j.title}</h3>
              <button onClick={() => setShowApply(false)} className="btn-ghost h-8 w-8 p-0"><X className="w-4 h-4" /></button>
            </div>
            <label className="label">Cover note (optional)</label>
            <textarea className="field min-h-[120px]" value={coverNote} onChange={(e) => setCoverNote(e.target.value)} placeholder="Tell the employer why you're a great fit…" />
            {err && <p className="text-sm text-brand-red mt-2">{err}</p>}
            <button onClick={apply} disabled={busy} className="btn-red w-full mt-3 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Submit application</button>
          </div>
        </div>
      )}
    </div>
  );
}

function OwnerApplicants({ jobId }: { jobId: string }) {
  const [data, setData] = useState<{ job: any; applications: any[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  async function load() { try { setData(await api.jobApplicants(jobId)); } catch { setData({ job: null, applications: [] }); } }
  useEffect(() => { load(); }, [jobId]);
  async function act(appId: string, status: "shortlisted" | "accepted" | "rejected") {
    setBusy(appId + status);
    try { await api.setApplicationStatus(appId, status); await load(); }
    catch (e: any) { alert(e?.message || "Could not update."); }
    finally { setBusy(null); }
  }
  if (!data) return <div className="card p-5 text-sm text-mut">Loading applicants…</div>;
  const badge: Record<string, string> = { applied: "bg-navy-50 text-navy", shortlisted: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-brand-red" };
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-navy">Applicants ({data.applications.length})</h3>
        {data.job && <span className="chip bg-navy-50 text-navy-600">{data.job.accepted}/{data.job.positions} hired{data.job.status === "closed" ? " · closed" : ""}</span>}
      </div>
      {data.applications.length === 0 ? (
        <p className="text-sm text-mut">No applicants yet.</p>
      ) : (
        <div className="space-y-2">
          {data.applications.map((a) => (
            <div key={a.id} className="p-3 rounded-lg border border-line">
              <div className="flex items-center justify-between gap-2">
                <Link href={`/u/${a.workerUserId}`} className="font-semibold text-navy hover:text-brand-red">{a.workerName}</Link>
                <span className={`chip capitalize ${badge[a.status] || "bg-navy-50"}`}>{a.status}</span>
              </div>
              {a.coverNote && <p className="text-sm text-mut mt-1">{a.coverNote}</p>}
              {a.status !== "accepted" && a.status !== "rejected" && (
                <div className="flex gap-2 mt-2">
                  {a.status !== "shortlisted" && <button onClick={() => act(a.id, "shortlisted")} disabled={busy === a.id + "shortlisted"} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">Shortlist</button>}
                  <button onClick={() => act(a.id, "accepted")} disabled={busy === a.id + "accepted"} className="btn-navy h-8 px-3 text-xs disabled:opacity-50">Accept</button>
                  <button onClick={() => act(a.id, "rejected")} disabled={busy === a.id + "rejected"} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
